import {
  createManagerOrderComment,
  getManagerOrderCommentLinks,
  getManagerOrderComments
} from '../../../api/commentApi';
import {
  type ICommentResponse,
  type ICreateCommentData,
  type ICommentListResponse,
  type ICommentLinksResponse,
  type ICommentLinksRequest
} from '@/types/IComment';
import { getErrorData, type IErrorData } from '../../../utils/getErrorData';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { isAxiosError } from 'axios';

export const getOrderCommentsThunk = createAsyncThunk<
  ICommentListResponse,
  { orderUuid: string; limit?: number; offset?: number; append?: boolean },
  { rejectValue: IErrorData }
>(
  'comments/getOrderCommentsThunk',
  async ({ orderUuid, limit, offset }, { rejectWithValue }) => {
    try {
      const response = await getManagerOrderComments(orderUuid, {
        limit,
        offset
      });

      return response;
    } catch (error) {
      /**
       * `404` здесь означает «комментариев нет», а не сбой: на заказ без
       * единой заметки бэкенд отвечает «По заказу нет комментариев» вместо
       * пустого списка. С PAR-5189 лента грузится для каждой открытой карточки
       * (счётчика в заказе больше нет), и без этой ветки блок пугал бы ошибкой
       * большинство заказов. Спутать с «заказа нет» нельзя: блок рисуется
       * только у загруженного заказа.
       */
      if (isAxiosError(error) && error.response?.status === 404) {
        return { items: [], meta: { total: 0 } };
      }

      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);

export const createOrderCommentThunk = createAsyncThunk<
  ICommentResponse,
  ICreateCommentData,
  { rejectValue: IErrorData }
>('comments/createOrderCommentThunk', async (data, { rejectWithValue }) => {
  try {
    const response = await createManagerOrderComment(data);

    return response;
  } catch (error) {
    const errorData = getErrorData(error);
    return rejectWithValue(errorData);
  }
});

export const getCommentLinksThunk = createAsyncThunk<
  { commentId: string; links: ICommentLinksResponse; orderUuid: string },
  { commentId: string; orderUuid: string; data: ICommentLinksRequest },
  { rejectValue: IErrorData }
>(
  'comments/fetchCommentLinks',
  async ({ commentId, orderUuid, data }, { rejectWithValue }) => {
    try {
      const response = await getManagerOrderCommentLinks(commentId, data);

      return { commentId, links: response, orderUuid };
    } catch (error) {
      const errorData = getErrorData(error);
      return rejectWithValue(errorData);
    }
  }
);
